import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { AppSpin } from '@/components/atoms';

import TreatmentReportFormSection from '@/components/organisms/TreatmentReportFormSection';
import TreatmentReportFormBottomBar from '@/pages/main/treatmentReport/components/TreatmentReportFormBottomBar';
import PatientPageHeader from '@/pages/main/patient/components/PatientPageHeader';
import { useTreatmentReportInit } from '@/hooks/main/useTreatmentReportInit';
import { useTreatmentReportList } from '@/hooks/main/useTreatmentReportList';
import { useCreateTreatmentReport } from '@/hooks/main/useCreateTreatmentReport';
import { useTreatmentReportRouteParams } from '@/hooks/main/useTreatmentReportRouteParams';
import { useFormChangeGuard } from '@/hooks/useFormChangeGuard';
import { authorizeNavigation } from '@/hooks/useFormConfirmationBlocker';
import useAppToast from '@/hooks/useAppToast';
import { japaneseDateToISO } from '@/utils/dateConverter';
import { useSearchTreatmentCount } from '@/hooks/main/useSearchTreatmentCount';
import {
  treatmentReportSchema,
  a2TreatmentReportSchema,
  type TreatmentReportFormData,
} from '@/schemas/treatmentReportSchema';
import type {
  TreatmentReportUpdatePayload,
  TreatmentReportListItem,
} from '@/services/main/treatmentReport';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';

export const CreateTreatmentReportPage: React.FC = () => {
  const { tenant } = useParams();
  const { serviceType: contextServiceType } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useAppToast();
  const copyFrom = (location.state as { copyFrom?: TreatmentReportListItem } | null)?.copyFrom;

  const isA2 = (contextServiceType?.toUpperCase() ?? '') === SERVICE_TYPE_A2;
  const schema = isA2 ? a2TreatmentReportSchema : treatmentReportSchema;

  const derived = useTreatmentReportRouteParams();

  const { data: listResponse } = useTreatmentReportList(derived.patientId);
  const createMutation = useCreateTreatmentReport(derived.patientId);
  const searchTreatmentCountMutation = useSearchTreatmentCount();
  const patient = listResponse?.data?.patient;

  // Active consent content drives both the inline display in the form and the
  // 変形徒手 / マッサージ classification used by the 5-month-gap FE validation.
  // Seeded with today's date; FormSection's 再取得 keeps it in sync with the
  // current treatment_report_date.
  const [activeConsentContent, setActiveConsentContent] = React.useState<string | null>(null);
  const fetchedInitialConsentForPatientRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!derived.patientId) return;
    if (fetchedInitialConsentForPatientRef.current === derived.patientId) return;
    fetchedInitialConsentForPatientRef.current = derived.patientId;

    const today = new Date().toISOString().slice(0, 10);
    searchTreatmentCountMutation
      .mutateAsync({
        patientId: derived.patientId,
        treatmentReportDate: today,
        secondFlag: false,
      })
      .then(res => {
        setActiveConsentContent(res.data?.active_consent_content ?? null);
      })
      .catch(() => {
        // allow retry on next render if initial request fails
        fetchedInitialConsentForPatientRef.current = null;
      });
  }, [derived.patientId, searchTreatmentCountMutation]);

  const emptyDefaults = useMemo(
    () => ({
      is_treatment_report_default: false,
      second_flag: false,
      non_print_cols: [] as string[],
      contents_frequency: '',
      status_course: '',
      remarks: '',
      treatment_report_date: '',
      treatment_yearmonth: undefined as any,
      massager_id: undefined as any,
      clinic_location_id: undefined as number | undefined,
      hospital_id: null as number | null,
      doctor_id: null as number | null,
      treatment_monthly_avg: 0 as number | undefined,
      location_name: '',
      postal_code1: '',
      postal_code2: '',
      address: '',
      telephone: '',
      fax: '',
      email: '',
    }),
    []
  );

  const defaultValues = useMemo(() => {
    if (!copyFrom) return emptyDefaults;
    return {
      ...emptyDefaults,
      // Only copy specific fields per spec Point 1
      hospital_id: copyFrom.hospital_id ?? null,
      doctor_id: copyFrom.doctor_id ?? null, // Implicitly part of hospital info
      contents_frequency: copyFrom.contents_frequency ?? '',
      treatment_monthly_avg: copyFrom.treatment_monthly_avg ?? 0,
      status_course: copyFrom.status_course ?? '',
      remarks: copyFrom.remarks ?? '',
      // Other fields should be empty/default
      is_treatment_report_default: copyFrom.is_treatment_report_default ?? false,
      non_print_cols: copyFrom.non_print_cols
        ? copyFrom.non_print_cols.split(',').map(k => (k === 'address3' ? 'location_name' : k))
        : [],
      // Massager info can be copied or left empty? 
      // Spec says: medical institution, content, frequency, status, notes.
      // Massager name is not mentioned as "copy from last", but image shows it empty.
    };
  }, [copyFrom, emptyDefaults]);

  const existingReports = listResponse?.data?.treatment_reports ?? [];

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    reset,
    formState: { isSubmitting },
  } = useTrimmedForm<TreatmentReportFormData>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const currentValues = watch();
  const watchedYm = watch('treatment_yearmonth');
  const { data: initResponse, isLoading: isLoadingInit } = useTreatmentReportInit(
    derived.patientId,
    { treatmentYearmonth: watchedYm }
  );
  const massagers = initResponse?.data?.massagers ?? [];
  const { clearFormChange, confirmIfChanged } = useFormChangeGuard(
    currentValues,
    defaultValues as any
  );

  const isBusy = isSubmitting || createMutation.isPending;

  const handleBack = useCallback(() => {
    if (isBusy) return;
    const target = `/${tenant}/treatment-reports/${derived.patientId}/list`;
    confirmIfChanged(() => {
      clearFormChange();
      authorizeNavigation();
      navigate(target);
    });
  }, [confirmIfChanged, clearFormChange, navigate, tenant, derived.patientId, isBusy]);

  /** FE validation: check duplicate yearmonth & 5-month gap before calling API */
  const validateYearmonthFE = useCallback(
    (data: TreatmentReportFormData): string | null => {
      const ym = data.treatment_yearmonth;
      if (!ym) return null;

      const sameYmReports = existingReports.filter(r => r.treatment_yearmonth === ym);

      // Max 2 per month
      if (sameYmReports.length >= 2) {
        return '入力された施術年月の施術報告書は既に２枚作成されています。';
      }

      // Duplicate (1 exists already)
      if (sameYmReports.length >= 1) {
        return '施術年月が重複しています。';
      }

      // 5-month gap — only when 施術報告書交付料を算定しない is unchecked,
      // and skipped entirely for 変形徒手 (R2 with orthomorphia consent).
      const isOrthoType = isA2 ? false : !!activeConsentContent?.includes('変形徒手');
      if (!data.is_treatment_report_default && !isOrthoType) {
        const sorted = [...existingReports]
          .filter(r => r.treatment_yearmonth && r.treatment_yearmonth < ym)
          .sort((a, b) => b.treatment_yearmonth - a.treatment_yearmonth);
        const prev = sorted[0];
        if (prev) {
          const prevYm = prev.treatment_yearmonth;
          const monthDiff =
            (Math.floor(ym / 100) - Math.floor(prevYm / 100)) * 12 + (ym % 100) - (prevYm % 100);
          if (monthDiff < 5) {
            return isA2
              ? '施術報告書交付料算定は、期間を5か月空けて設定してください。'
              : '施術がマッサージの場合の施術報告書交付料算定は、期間を5か月空けて設定してください。';
          }
        }
      }

      return null;
    },
    [existingReports, isA2, activeConsentContent]
  );

  const onSubmit = useCallback(
    async (data: TreatmentReportFormData) => {
      // FE yearmonth validation (before API call)
      if (!data.second_flag) {
        const feError = validateYearmonthFE(data);
        if (feError) {
          setError('treatment_yearmonth', { message: feError });
          toast.error('更新ができませんでした。');
          return;
        }
      }
      // BD HIT-4357: 施術の内容 comment is optional. If left empty, the printed
      // 施術の内容 is positions-only — warn the user before saving.
      if (!(data.contents_frequency ?? '').trim()) {
        if (!window.confirm('施術報告書の「施術の内容」は施術部位のみです。よろしいですか？')) {
          return;
        }
      }
      const { location_name, clinic_location_id, ...rest } = data;
      // Convert wareki date to ISO for API
      const payload: TreatmentReportUpdatePayload = {
        ...rest,
        clinic_location_id: clinic_location_id ?? null,
        address3: location_name ?? null,
        non_print_cols: rest.non_print_cols?.map(k => (k === 'location_name' ? 'address3' : k)),
        second_flag: rest.second_flag ?? undefined,
        treatment_report_date: rest.treatment_report_date?.includes('年')
          ? japaneseDateToISO(rest.treatment_report_date)
          : rest.treatment_report_date,
      };
      try {
        const res = await createMutation.mutateAsync(payload);
        reset(emptyDefaults as any);
        clearFormChange();
        authorizeNavigation();
        toast.success('更新が正常に終了しました。');
        const newId = (res.data as any)?.id;
        if (newId) {
          navigate(`/${tenant}/treatment-reports/${derived.patientId}/edit/${newId}`);
        } else {
          navigate(`/${tenant}/treatment-reports/${derived.patientId}/list`);
        }
      } catch (err: unknown) {
        const fieldErrors = (err as { response?: { data?: { errors?: Record<string, string[]> } } })
          ?.response?.data?.errors;
        if (fieldErrors && typeof fieldErrors === 'object') {
          Object.entries(fieldErrors).forEach(([key, msgs]) => {
            if (Array.isArray(msgs) && msgs[0]) {
              // BE returns the consent-form range error under field treatment_report_date,
              // but BD requires displaying it inline under field 施術年月.
              const targetField = key === 'treatment_report_date' ? 'treatment_yearmonth' : key;
              setError(targetField as keyof TreatmentReportFormData, { message: msgs[0] });
            }
          });
        }
        toast.error('更新ができませんでした。');
      }
    },
    [
      createMutation,
      clearFormChange,
      validateYearmonthFE,
      setError,
      toast,
      navigate,
      tenant,
      derived.patientId,
    ]
  );

  if (isLoadingInit) {
    return (
      <div className="flex justify-center p-10">
        <AppSpin size="large" />
      </div>
    );
  }

  const formId = 'treatment-report-create-form';

  return (
    <div>
      <PatientPageHeader
        patient={patient}
        patientId={derived.patientId}
        tenant={tenant || ''}
        navigate={navigate}
        activeKey="treatment-reports"
        disabled={isBusy}
      />

      <form
        id={formId}
        onSubmit={handleSubmit(onSubmit, () => toast.error('更新ができませんでした。'))}
      >
        <TreatmentReportFormSection
          control={control}
          watch={watch}
          setValue={setValue}
          formMode="create"
          massagers={massagers}
          disabled={isBusy}
          existingReports={existingReports}
          patientId={derived.patientId}
          isCopying={!!copyFrom}
          activeConsentContent={activeConsentContent ?? initResponse?.data?.active_consent_content}
          onActiveConsentChange={setActiveConsentContent}
        />
      </form>

      <TreatmentReportFormBottomBar
        mode="create"
        isSubmitting={isBusy}
        formId={formId}
        onBack={handleBack}
      />
    </div>
  );
};
